import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAdminExamPins } from "../store/admin";
import {
  formatDate,
  pinLabel,
  statusLabel,
  examTypeLabel,
  orderReference,
  statusBadgeClass,
  examTypeBadgeClass,
  STATUS_OPTIONS,
  EXAM_TYPE_OPTIONS,
  SORT_OPTIONS,
} from "./components/examPins/examPinUtils";
import {
  AdminPageBody,
  AdminSectionHeader,
  AdminTablePanel,
  AdminLoadingState,
  AdminEmptyState,
  adminButton,
  adminPrimaryButton,
} from "../components/AdminPageShell";
import AdminNavBar from "../components/AdminNavBar";
import StaffPagination from "./components/staff/StaffPagination";
import Toast from "../components/Toast";
import {useConfirmDialog} from "../components/ConfirmDialogProvider";

const ExamPins = () => {
  const confirm = useConfirmDialog();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const typeParam = searchParams.get("examType");
  const activeExamType = typeParam || "";

  const {
    examPins = [],
    examPinsLoading: loading,
    examPinsError: error,
    examPinTotal: total,
    examPinPages: totalPages,
    fetchExamPins,
    deleteExamPin,
    examPinMutationLoading: mutationLoading,
    createExamPin,
    examPinStats,
    fetchExamPinStats,
    examPinDetail,
    examPinDetailLoading,
    examPinDetailError,
    fetchExamPinById,
    examPinDeliveries,
    examPinDeliveriesLoading,
    examPinDeliveriesError,
    examPinDeliveryTotal,
    examPinDeliveryPages,
    fetchExamPinDeliveries,
  } = useAdminExamPins();

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sort, setSort] = useState("newest");
  const [showUpload, setShowUpload] = useState(false);
  const [uploadPins, setUploadPins] = useState("");
  const [uploadExamType, setUploadExamType] = useState("WAEC");
  const [uploadError, setUploadError] = useState("");

  const [detailOpen, setDetailOpen] = useState(false);
  const [deliveriesOpen, setDeliveriesOpen] = useState(false);
  const [deliveriesPage, setDeliveriesPage] = useState(1);
  const [deliveryStatus, setDeliveryStatus] = useState("");

  const [toast, setToast] = useState(null);
  const [isSuccess, setIsSuccess] = useState(true);
  const showToast = useCallback((message, success = true) => {
    setToast(message);
    setIsSuccess(success);
    setTimeout(() => setToast(null), 5000);
  }, []);

  const page = Number(searchParams.get("page")) || 1;
  const limit = Number(searchParams.get("limit")) || 10;

  useEffect(() => {
    const load = async () => {
      const result = await fetchExamPins({
        page,
        limit,
        search,
        status: statusFilter,
        examType: activeExamType,
        sort,
      });
      if (!result.success) {
        showToast(result.error || "Failed to fetch exam pins", false);
      }
    };
    load();
  }, [page, limit, search, statusFilter, activeExamType, sort, fetchExamPins, showToast]);

  useEffect(() => {
    fetchExamPinStats({ examType: activeExamType });
  }, [activeExamType, fetchExamPinStats]);

  const handlePageChange = (newPage) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("page", String(newPage));
    nextParams.set("limit", String(limit));
    navigate({ search: nextParams.toString() });
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const v = e.target.search.value;
    setSearch(v);
    if (page !== 1) navigate({ search: `?page=1` });
  };

  const handleDelete = async (pin) => {
    return confirm({
      title: `Delete pin ${pinLabel(pin)}?`,
      description: "This action cannot be undone.",
      confirmLabel: "Delete pin",
      onConfirm: async () => {
        const result = await deleteExamPin(pin.id);
        if (!result.success) throw new Error(result.error || "Failed to delete exam pin.");
        showToast("Exam pin deleted successfully", true);
        await fetchExamPins({ page, limit, search, status: statusFilter, examType: activeExamType, sort });
      },
    });
  };

  const handleView = async (pin) => {
    setDetailOpen(true);
    await fetchExamPinById(pin.id);
  };

  const loadDeliveries = async (nextPage = deliveriesPage, status = deliveryStatus) => {
    setDeliveriesPage(nextPage);
    await fetchExamPinDeliveries({
      page: nextPage,
      limit: 20,
      status: status || undefined,
    });
  };

  const openDeliveries = () => {
    setDeliveriesOpen(true);
    loadDeliveries(1, deliveryStatus);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setUploadError("");
    const pins = uploadPins
      .split("\n")
      .map((p) => p.trim())
      .filter(Boolean);
    if (pins.length === 0) {
      setUploadError("Please enter at least one PIN.");
      return;
    }
    if (!uploadExamType) {
      setUploadError("Please select an exam type.");
      return;
    }
    const result = await createExamPin({ pins, examType: uploadExamType });
    if (result.success) {
      setShowUpload(false);
      setUploadPins("");
      setUploadExamType("WAEC");
      showToast("Exam pins uploaded successfully", true);
      fetchExamPins({ page, limit, search, status: statusFilter, examType: activeExamType, sort });
    } else {
      setUploadError(result.error || "Failed to create exam pins.");
    }
  };

  return (
    <div>
      <AdminNavBar
        title="Exam Pins"
        subTitle="Manage WAEC, NECO, and NABTEB exam pins"
        context={`${total} pins`}
      />
    <AdminPageBody>
      <AdminSectionHeader
        title="Exam Pins"
        description="Manage WAEC, NECO, and NABTEB exam pins"
        count={total}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={openDeliveries} className={adminButton}>
              Deliveries
            </button>
            <button
              onClick={() => navigate("/admin/exam-pins/price")}
              className={adminButton}
            >
              Set Price
            </button>
            <button
              onClick={() => setShowUpload(true)}
              className={adminPrimaryButton}
            >
              Upload Pins
            </button>
          </div>
        }
      />

      {toast && (
        <Toast
          message={toast}
          isSuccess={isSuccess}
          onClose={() => setToast(null)}
        />
      )}

      {examPinStats && (
        <div className="mb-6 grid gap-4 sm:grid-cols-3">
          <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm">
            <div className="text-2xl font-bold text-slate-900">{examPinStats.total ?? 0}</div>
            <div className="text-xs text-slate-500">Total Pins</div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm">
            <div className="text-2xl font-bold text-emerald-600">{examPinStats.available ?? 0}</div>
            <div className="text-xs text-slate-500">Available</div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm">
            <div className="text-2xl font-bold text-blue-600">{examPinStats.sold ?? 0}</div>
            <div className="text-xs text-slate-500">Sold</div>
          </div>
        </div>
      )}

      <div className="mb-4 flex flex-wrap gap-3">
        <form onSubmit={handleSearchSubmit} className="flex gap-2">
          <input
            name="search"
            type="text"
            placeholder="Search by pin or reference..."
            className="h-10 w-64 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#16213A]"
          />
          <button
            type="submit"
            className={adminButton}
          >
            Search
          </button>
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className={adminButton}
            >
              Clear
            </button>
          )}
        </form>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-10 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#16213A]"
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        <select
          value={activeExamType}
          onChange={(e) => {
            const nextParams = new URLSearchParams(searchParams);
            nextParams.set("page", "1");
            if (e.target.value) {
              nextParams.set("examType", e.target.value);
            } else {
              nextParams.delete("examType");
            }
            navigate({ search: nextParams.toString() });
          }}
          className="h-10 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#16213A]"
        >
          {EXAM_TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="h-10 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#16213A]"
        >
          {SORT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">
          {error}
        </div>
      )}

      {loading ? (
        <AdminLoadingState />
      ) : examPins.length === 0 ? (
        <AdminEmptyState
          title="No exam pins found"
          description="Try adjusting your search or filter criteria."
        />
      ) : (
        <AdminTablePanel>
          <div className="overflow-x-auto">
          <table className="w-full min-w-[620px]">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="px-3 py-1.5 text-left text-[11px] font-semibold text-slate-600">PIN</th>
                <th className="px-3 py-1.5 text-left text-[11px] font-semibold text-slate-600">Status</th>
                <th className="px-3 py-1.5 text-left text-[11px] font-semibold text-slate-600">Exam Type</th>
                <th className="px-3 py-1.5 text-left text-[11px] font-semibold text-slate-600">Created</th>
                <th className="px-3 py-1.5 text-left text-[11px] font-semibold text-slate-600">Sold To</th>
                <th className="px-3 py-1.5 text-left text-[11px] font-semibold text-slate-600">Order Ref</th>
                <th className="px-3 py-1.5 text-right text-[11px] font-semibold text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {examPins.map((pin) => (
                <tr key={pin.id} className="border-b border-slate-100">
                  <td className="px-3 py-1.5 text-xs">
                    <code className="text-xs">{pinLabel(pin)}</code>
                  </td>
                  <td className="px-3 py-1.5 text-xs">
                    <span className={`badge ${statusBadgeClass(pin.status)}`}>
                      {statusLabel(pin)}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-xs">
                    <span className={`badge ${examTypeBadgeClass(pin.examType)}`}>
                      {examTypeLabel(pin)}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-xs whitespace-nowrap">{formatDate(pin.createdAt)}</td>
                  <td className="px-3 py-1.5 text-xs">{pin.soldToEmail || "--"}</td>
                  <td className="px-3 py-1.5 text-xs whitespace-nowrap">{orderReference(pin)}</td>
                  <td className="px-3 py-1.5 text-xs text-right">
                    <div className="flex items-center justify-end gap-3">
                      <button
                        onClick={() => handleView(pin)}
                        className="text-xs font-semibold text-[#16213A] hover:text-[#203052]"
                      >
                        View
                      </button>
                      <button
                        onClick={() => handleDelete(pin)}
                        disabled={mutationLoading}
                        className="text-xs font-semibold text-red-600 hover:text-red-800"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </AdminTablePanel>
      )}

      {totalPages > 1 && (
        <StaffPagination
          page={page}
          lastPage={totalPages}
          onPageChange={handlePageChange}
        />
      )}

      {showUpload && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-96 rounded-lg bg-white p-6">
            <h3 className="mb-4 text-lg font-bold">Upload Exam Pins</h3>
            <form onSubmit={handleUpload} className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium">Exam Type</label>
                <select
                  value={uploadExamType}
                  onChange={(e) => setUploadExamType(e.target.value)}
                  className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#16213A]"
                >
                  {EXAM_TYPE_OPTIONS.filter((o) => o.value).map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">
                  PINs (one per line)
                </label>
                <textarea
                  value={uploadPins}
                  onChange={(e) => setUploadPins(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#16213A]"
                  placeholder="Enter each PIN on a new line"
                  rows={6}
                  required
                />
              </div>
              {uploadError && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">
                  {uploadError}
                </div>
              )}
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowUpload(false)}
                  className={adminButton}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={mutationLoading}
                  className={adminPrimaryButton}
                >
                  {mutationLoading ? "Uploading..." : "Upload"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {detailOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-lg rounded-lg bg-white p-6">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-bold">Exam PIN Details</h3>
              <button
                type="button"
                onClick={() => setDetailOpen(false)}
                className="text-slate-400 hover:text-slate-700"
              >
                ✕
              </button>
            </div>
            {examPinDetailLoading ? (
              <AdminLoadingState />
            ) : examPinDetailError ? (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">
                {examPinDetailError}
              </div>
            ) : examPinDetail ? (
              <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                <div>
                  <dt className="text-xs font-semibold uppercase text-slate-500">PIN</dt>
                  <dd className="font-mono">{pinLabel(examPinDetail)}</dd>
                </div>
                <div>
                  <dt className="text-xs font-semibold uppercase text-slate-500">Status</dt>
                  <dd>{statusLabel(examPinDetail)}</dd>
                </div>
                <div>
                  <dt className="text-xs font-semibold uppercase text-slate-500">Exam Type</dt>
                  <dd>{examTypeLabel(examPinDetail)}</dd>
                </div>
                <div>
                  <dt className="text-xs font-semibold uppercase text-slate-500">Order</dt>
                  <dd>{orderReference(examPinDetail)}</dd>
                </div>
                <div>
                  <dt className="text-xs font-semibold uppercase text-slate-500">Sold To</dt>
                  <dd>{examPinDetail.soldToEmail || "—"}</dd>
                </div>
                <div>
                  <dt className="text-xs font-semibold uppercase text-slate-500">Sold At</dt>
                  <dd>{formatDate(examPinDetail.soldAt)}</dd>
                </div>
                <div>
                  <dt className="text-xs font-semibold uppercase text-slate-500">Updated</dt>
                  <dd>{formatDate(examPinDetail.updatedAt)}</dd>
                </div>
                <div>
                  <dt className="text-xs font-semibold uppercase text-slate-500">Created</dt>
                  <dd>{formatDate(examPinDetail.createdAt)}</dd>
                </div>
              </dl>
            ) : null}
          </div>
        </div>
      )}

      {deliveriesOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="flex max-h-[85vh] w-full max-w-5xl flex-col rounded-lg bg-white p-6">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-lg font-bold">Exam PIN Deliveries</h3>
                <p className="text-xs text-slate-500">{examPinDeliveryTotal} deliveries</p>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={deliveryStatus}
                  onChange={(event) => {
                    setDeliveryStatus(event.target.value);
                    loadDeliveries(1, event.target.value);
                  }}
                  className="h-9 rounded-lg border border-slate-300 px-3 text-sm"
                >
                  <option value="">All statuses</option>
                  <option value="SENT">Sent</option>
                  <option value="FAILED">Failed</option>
                </select>
                <button
                  type="button"
                  onClick={() => setDeliveriesOpen(false)}
                  className="text-slate-400 hover:text-slate-700"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-auto">
              {examPinDeliveriesLoading ? (
                <AdminLoadingState />
              ) : examPinDeliveriesError ? (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">
                  {examPinDeliveriesError}
                </div>
              ) : examPinDeliveries.length === 0 ? (
                <AdminEmptyState title="No deliveries found" />
              ) : (
                <table className="w-full min-w-[720px] text-left text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600">Recipient</th>
                      <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600">Type</th>
                      <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600">Status</th>
                      <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600">Order</th>
                      <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600">Attempts</th>
                      <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {examPinDeliveries.map((delivery) => (
                      <tr key={delivery.id} className="border-b border-slate-100">
                        <td className="px-3 py-2">
                          <div>{delivery.recipient}</div>
                          {delivery.lastError && (
                            <div className="text-xs text-red-500">{delivery.lastError}</div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs">{delivery.emailType}</td>
                        <td className="px-3 py-2">
                          <span className={`badge ${statusBadgeClass(delivery.status)}`}>
                            {delivery.status}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-xs">{delivery.order?.reference || "—"}</td>
                        <td className="px-3 py-2 text-xs">{delivery.attempts}</td>
                        <td className="px-3 py-2 text-xs">{formatDate(delivery.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {examPinDeliveryPages > 1 && (
              <div className="mt-4">
                <StaffPagination
                  page={deliveriesPage}
                  lastPage={examPinDeliveryPages}
                  onPageChange={(nextPage) => loadDeliveries(nextPage, deliveryStatus)}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </AdminPageBody>
    </div>
  );
};

export default ExamPins;
